import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';

interface SEOMetaProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  ogType?: string;
  twitterCard?: string;
  twitterSite?: string;
  twitterCreator?: string;
  twitterImage?: string;
  twitterImageAlt?: string;
  canonicalUrl?: string;
}

const languages = [
  'en', 'ar', 'zh', 'fr', 'de', 'hi', 'it', 'ja', 'ko', 'pt', 'ru', 'es', 'tr',
  'nl', 'pl', 'vi', 'id', 'th', 'uk', 'ro', 'el', 'cs', 'sv', 'hu', 'fi', 'da', 'no', 'he'
];
const domain = 'https://solanavolumebot.io';

/**
 * SEO Meta Component for YMYL (Your Money Your Life) compliant websites
 * This component implements best practices for SEO and structured data
 * according to W3C standards and YMYL guidelines
 */
export const SEOMeta: React.FC<SEOMetaProps> = ({
  title = 'Solana Volume Bot: SOL Volume Bot - Pump Fun Volume Bot',
  description = 'Solana Volume Bot is an automated Solana volume bot designed to help projects boost their token visibility on the Solana blockchain. Get started with our easy-to-use platform today.',
  keywords = 'solana, automated solana volume bot, volume bot, token visibility, defi, solana bot, volume distribution, DEX ranking',
  ogTitle,
  ogDescription,
  ogImage = 'https://solana-volumebot.com/og-image.jpg',
  ogUrl,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  twitterSite = '@solana_volumebot',
  twitterCreator = '@solana_volumebot',
  twitterImage = 'https://solana-volumebot.com/twitter-image.jpg',
  twitterImageAlt = 'Solana Volume Bot',
  canonicalUrl,
}) => {
  const { t } = useTranslation();
  const siteTitle = 'Solana Volume Bot';
  const fullTitle = title === siteTitle ? title : `${title} | ${siteTitle}`;
  const ogTitleFinal = ogTitle || fullTitle;
  const ogDescriptionFinal = ogDescription || description;
  const ogUrlFinal = ogUrl || (typeof window !== 'undefined' ? window.location.href : '');
  const canonicalUrlFinal = canonicalUrl || ogUrlFinal;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: siteTitle,
    description: description,
    url: ogUrlFinal,
    applicationCategory: 'FinanceApplication',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD'
    }
  };

  // Structured data for logo and hero images
  const logoImageObject = {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    "contentUrl": "https://solanavolumebot.io/logo.webp",
    "url": "https://solanavolumebot.io/logo.webp",
    "license": "https://solanavolumebot.io/license",
    "acquireLicensePage": "https://solanavolumebot.io/contact",
    "creditText": "Solana Volume Bot"
  };
  const heroImageObject = {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    "contentUrl": "https://solanavolumebot.io/images/branding/SOLANA-VOLUME-BOT-MASCOTTE.webp",
    "url": "https://solanavolumebot.io/images/branding/SOLANA-VOLUME-BOT-MASCOTTE.webp",
    "license": "https://solanavolumebot.io/license",
    "acquireLicensePage": "https://solanavolumebot.io/contact",
    "creditText": "Solana Volume Bot"
  };

  return (
    <Helmet>
      {/* Primary Meta Tags - YMYL Optimized */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="Solana Volume Bot Team" />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <link rel="canonical" href={canonicalUrlFinal} />
      
      {/* Accessibility and Language */}
      {/* Removed <html lang="en" /> as it is not valid in Helmet */}
      
      {/* Performance & Device Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      
      {/* Security Tags - YMYL Trust Indicators moved to server headers, removed from HTML */}
      
      {/* Open Graph Tags */}
      <meta property="og:title" content={ogTitleFinal} />
      <meta property="og:description" content={ogDescriptionFinal} />
      <meta property="og:image" content="https://solanavolumebot.io/logo.webp" />
      <meta property="og:url" content={ogUrlFinal} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="Solana Volume Bot" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:updated_time" content={new Date().toISOString()} />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:site" content={twitterSite} />
      <meta name="twitter:creator" content={twitterCreator} />
      <meta name="twitter:title" content={ogTitleFinal} />
      <meta name="twitter:description" content={ogDescriptionFinal} />
      <meta name="twitter:image" content="https://solanavolumebot.io/logo.webp" />
      <meta name="twitter:image:alt" content={twitterImageAlt} />
      
      {/* YMYL Trust & Professional Indication Tags */}
      <meta name="theme-color" content="#ffffff" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="application-name" content="Solana Volume Bot" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="HandheldFriendly" content="True" />
      <meta name="MobileOptimized" content="320" />
      
      {/* Web App Manifest - W3C Standard */}
      <link rel="manifest" href="/site.webmanifest" />
      
      {/* Cache Control - Browser Optimization */}
      <meta httpEquiv="Cache-Control" content="max-age=86400, public" />
      
      {/* Accessibility Tags */}
      <meta name="accessibility" content="WCAG2AA" />
      
      {/* Favicon and App Icons */}
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(logoImageObject)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(heroImageObject)}
      </script>

      {/* i18next translations */}
      <title>{t('seo.home_title')}</title>
      <meta name="description" content={t('seo.home_description')} />
      <link rel="alternate" hrefLang="en" href={domain + '/'} />
      {languages.filter(l => l !== 'en').map(l => (
        <link key={l} rel="alternate" hrefLang={l} href={`${domain}/${l}/`} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={domain + '/'} />
    </Helmet>
  );
};
